import React, { useEffect, useMemo, useRef, useState } from "react"
import { Bars3Icon, ChevronRightIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import useT from "../Storefront/hooks/useT"
import { useCategories } from "../Storefront/hooks/useCatalogData"
import { buildCategoryUrl, childCategories, hasProducts, rootCategories } from "../Storefront/utils/categories"
import { CategoryCovers } from "../Storefront/api"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"

// how long the pointer rests on the button before the menu opens, and how long it may be away
// before it closes (a pass over the button on the way elsewhere opens nothing)
const OPEN_DELAY = 180
const CLOSE_DELAY = 280
// a subcategory group shows this many of its own children, then "Show more"
const PER_GROUP = 8
// menu aim: while the pointer heads for the panel on the right, a category it crosses on the way
// waits this long before it becomes the active one (re-checked then: still heading there, it waits again)
const AIM_DELAY = 300
// how far above / below the panel the aim's triangle reaches (px)
const AIM_TOLERANCE = 75
const slope = (a, b) => (b.y - a.y) / (b.x - a.x)

/** The first URL of a category's thumb (a srcset list), for the small picture beside its name. */
const thumbOf = (category) => {
  const set = category?.thumb?.default
  const first = Array.isArray(set) ? set[0] : set
  return typeof first === "string" ? first.split(",")[0].trim().split(" ")[0] : null
}

const shown = (list) => list.filter(hasProducts)
// a top category opens the panel beside the list only when it has more than one subcategory to
// show: with one (or none) its own page is the place to go
const panelGroups = (list, categoryId) => {
  const groups = shown(childCategories(list, categoryId))
  return groups.length > 1 ? groups : []
}

/**
 * The desktop "Categories" button with a menu that opens on hover, the way the big shops do it
 * (eMAG): the top categories down the left, the one under the pointer's subcategories on the
 * right — each a heading with its own subcategories under it. The pointer moving towards the
 * right panel does not switch the category it passes over on the way (menu aim: the move counts
 * as heading for the panel when it is mostly to the right). A click on the button still opens the
 * categories page; the menu closes on a link, on Escape and when the pointer has left it.
 */
export default function CategoriesMenu() {
  const t = useT()
  const { list } = useCategories()
  const roots = useMemo(() => shown(rootCategories(list)), [list])
  const [open, setOpen] = useState(false)
  const [activeId, setActiveId] = useState(null)
  // the cover of each top category's newest product, asked for the first time the menu opens
  const language = useSiteLanguage()
  const [covers, setCovers] = useState({})
  const coversFor = useRef(null)
  useEffect(() => {
    if (!open || !roots.length) return
    const key = `${language}:${roots.map((root) => root.categoryId).join(",")}`
    if (coversFor.current === key) return
    coversFor.current = key
    CategoryCovers(roots.map((root) => root.categoryId), language)
      .then((res) => res?.covers && setCovers(res.covers))
      .catch(() => {})
  }, [open, roots, language])
  const openTimer = useRef(null)
  const closeTimer = useRef(null)
  const switchTimer = useRef(null)
  // the pointer's last few positions (menu aim), the panel beside the list, where the last delay was decided
  const trail = useRef([])
  const panelRef = useRef(null)
  const delayedAt = useRef(null)

  const clear = () => {
    clearTimeout(openTimer.current)
    clearTimeout(closeTimer.current)
  }
  const enter = () => {
    clearTimeout(closeTimer.current)
    if (open) return
    openTimer.current = setTimeout(() => setOpen(true), OPEN_DELAY)
  }
  const leave = () => {
    clearTimeout(openTimer.current)
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY)
  }
  const close = () => {
    clear()
    clearTimeout(switchTimer.current)
    trail.current = []
    setOpen(false)
  }
  useEffect(() => () => {
    clear()
    clearTimeout(switchTimer.current)
  }, [])
  useEffect(() => {
    if (!open) return
    const onKey = (e) => e.key === "Escape" && close()
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open])
  // the menu opens on the first category that has subcategories
  useEffect(() => {
    if (open && !roots.some((root) => root.categoryId === activeId)) setActiveId((roots.find((root) => panelGroups(list, root.categoryId).length) || roots[0])?.categoryId || null)
  }, [open, roots])

  // menu aim (as eMAG / Amazon): moving up and down the list switches at once; moving sideways
  // towards the panel — the pointer inside the triangle from where it was to the panel's top and
  // bottom corners — keeps the open category while it crosses others on the way, then switches
  // only if it has stopped on one
  const aimDelay = () => {
    const panel = panelRef.current
    const points = trail.current
    if (!panel || points.length < 2) return 0
    const loc = points[points.length - 1]
    const prev = points[0]
    // stopped where the last delay was decided: the pointer rests on this row
    if (delayedAt.current && loc.x === delayedAt.current.x && loc.y === delayedAt.current.y) return 0
    const rect = panel.getBoundingClientRect()
    const upper = { x: rect.left, y: rect.top - AIM_TOLERANCE }
    const lower = { x: rect.left, y: rect.bottom + AIM_TOLERANCE }
    // on the panel already, or coming from it: nothing to aim at
    if (loc.x >= rect.left || prev.x >= rect.left) return 0
    const towardsPanel = slope(loc, upper) < slope(prev, upper) && slope(loc, lower) > slope(prev, lower)
    if (!towardsPanel) {
      delayedAt.current = null
      return 0
    }
    delayedAt.current = loc
    return AIM_DELAY
  }
  const hoverRow = (categoryId) => {
    clearTimeout(switchTimer.current)
    const tryActivate = () => {
      const wait = aimDelay()
      if (!wait) return setActiveId(categoryId)
      switchTimer.current = setTimeout(tryActivate, wait)
    }
    tryActivate()
  }
  const track = (event) => {
    trail.current = [...trail.current.slice(-2), { x: event.clientX, y: event.clientY }]
  }

  const active = roots.find((root) => root.categoryId === activeId)
  const groups = active ? panelGroups(list, active.categoryId) : []

  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave} data-testid="categories-menu">
      <Button variant="primary_s" href="/categories" className="min-w-[165px]" onClick={close} aria-haspopup="true" aria-expanded={open}>
        <span className="flex items-center gap-2">
          <Bars3Icon className="w-5 h-5" aria-hidden="true" />
          {t`Categories`}
        </span>
      </Button>
      {open && roots.length > 0 && (
        // under the bar, as wide as the page's content
        <div
          className="fixed left-0 right-0 top-[108px] z-[398] px-[60px] pointer-events-none"
          onMouseMove={track}
        >
          <div className="mx-auto max-w-w1_xl">
          {/* a category without subcategories opens no panel beside the list: the menu is the list alone */}
          <div
            className={`pointer-events-auto flex bg-paper border border-bordered rounded-b-xl shadow-2xl overflow-hidden max-h-[calc(100vh-130px)] ${groups.length ? "w-full" : "w-fit"}`}
            data-testid="categories-menu-panel"
          >
            {/* the top categories */}
            <ul className={`m-0 p-2 list-none w-[300px] shrink-0 overflow-y-auto scrollbar-thin scrollbar-track-paper scrollbar-thumb-neutral_2 bg-white ${groups.length ? "border-r border-bordered" : ""}`}>
              {roots.map((root) => {
                const on = root.categoryId === activeId
                const thumb = covers[root.categoryId] || thumbOf(root)
                return (
                  <li key={root.categoryId} onMouseEnter={() => hoverRow(root.categoryId)}>
                    <Button
                      variant="none"
                      href={buildCategoryUrl(root.categoryId, list)}
                      onClick={close}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] no-underline ${on ? "bg-neutral text-primary font-semibold" : "text-text hover:bg-neutral"}`}
                      data-testid="categories-menu-root"
                    >
                      {/* its newest product's cover, else its own picture, else its initial */}
                      {thumb ? (
                        <img src={thumb} alt="" className="w-9 h-9 rounded object-cover shrink-0 bg-neutral" loading="lazy" />
                      ) : (
                        <span className={`w-9 h-9 rounded shrink-0 flex items-center justify-center text-[13px] font-bold ${on ? "bg-primary text-white" : "bg-neutral text-text_secondary"}`}>{String(root.name || "?").trim().charAt(0).toUpperCase()}</span>
                      )}
                      <span className="flex-1 min-w-0 truncate">{root.name}</span>
                      {panelGroups(list, root.categoryId).length > 0 && <ChevronRightIcon className="w-4 h-4 shrink-0 text-text_secondary" />}
                    </Button>
                  </li>
                )
              })}
              <li className="mt-1 border-t border-bordered pt-1">
                <Button variant="none" href="/categories" onClick={close} className="flex items-center px-3 py-2.5 text-[14px] font-semibold text-primary no-underline hover:underline">
                  {t`All categories`}
                </Button>
              </li>
            </ul>

            {/* the active one's subcategories: each a heading with its own under it, packed in
                balanced columns (each group in the next free place, not in rows of equal height) */}
            {groups.length > 0 && <div ref={panelRef} onMouseEnter={() => clearTimeout(switchTimer.current)} className="flex-1 min-w-0 overflow-y-auto scrollbar-thin scrollbar-track-paper scrollbar-thumb-neutral_2 px-8 py-6">
              {active && (
                <Button variant="none" href={buildCategoryUrl(active.categoryId, list)} onClick={close} className="inline-block mb-5 text-[18px] font-bold text-text no-underline hover:text-primary">
                  {t("All in %name%", active.name)} <ChevronRightIcon className="inline w-4 h-4 -mt-0.5" />
                </Button>
              )}
              {(
                <div className="columns-3 gap-x-10">
                  {groups.map((group) => {
                    const items = shown(childCategories(list, group.categoryId))
                    return (
                      <div key={group.categoryId} className="min-w-0 break-inside-avoid mb-7" data-testid="categories-menu-group">
                        <Button variant="none" href={buildCategoryUrl(group.categoryId, list)} onClick={close} className="block mb-2 text-[15px] font-bold text-text no-underline hover:text-primary">
                          {group.name}
                        </Button>
                        {items.slice(0, PER_GROUP).map((item) => (
                          <Button variant="none" key={item.categoryId} href={buildCategoryUrl(item.categoryId, list)} onClick={close} className="block py-1 text-[14px] text-text no-underline hover:text-primary truncate">
                            {item.name}
                          </Button>
                        ))}
                        {items.length > PER_GROUP && (
                          <Button variant="none" href={buildCategoryUrl(group.categoryId, list)} onClick={close} className="block py-1 text-[14px] font-semibold text-primary no-underline hover:underline">
                            {t`Show more`}
                          </Button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>}
          </div>
          </div>
        </div>
      )}
    </div>
  )
}
